import { memo, useCallback, useMemo, useState } from 'react';
import { useForm, Controller, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ApolloCache, gql, useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { isEqual } from 'lodash';
import { up } from 'styled-breakpoints';
import styled from 'styled-components';
import * as zod from 'zod';
import {
  Mutation,
  MutationCreateContactNoteArgs,
  MutationDeleteContactNoteArgs,
  MutationUpdateContactNoteArgs,
  Query,
  QueryContactNoteArgs,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { useShowConfirmationModal } from 'src/components/general/feedback/hooks/use-show-confirmation-modal';
import { useShowNotification } from 'src/components/general/feedback/hooks/use-show-notification';
import {
  BigTextArea,
  InlineInputWrapper,
} from 'src/components/general/inputs/inline-text-inputs';
import { InlineFormModal } from 'src/components/general/modals/inline-form/inline-form-modal';
import {
  InlineFormContactSelect,
  InlineFormDeleteOption,
  useShowCloseConfirmationModal,
} from 'src/components/general/modals/inline-form/shared';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import { useAuthorization } from 'src/hooks/use-authorization';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useCurrentUser } from 'src/hooks/use-current-user';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { useModalUrl } from 'src/hooks/use-modal-url';
import { useUrls } from 'src/hooks/use-urls';
import { useVisible } from 'src/hooks/use-visible';
import { copyUrlToClipboard } from 'src/utils/copy-to-clipboard-helpers';

const CREATE_CONTACT_NOTE_MUTATION = gql`
  mutation CreateContactNote($institutionId: Int!, $input: CreateContactNoteInput!) {
    createContactNote(institutionId: $institutionId, input: $input) {
      contactNote {
        id
        text
      }
    }
  }
`;

const UPDATE_CONTACT_NOTE_MUTATION = gql`
  mutation UpdateContactNote($input: UpdateContactNoteInput!) {
    updateContactNote(input: $input) {
      contactNote {
        id
        text
      }
    }
  }
`;

const DELETE_CONTACT_NOTE_MUTATION = gql`
  mutation DeleteContactNote($input: DeleteContactNoteInput!) {
    deleteContactNote(input: $input) {
      success
    }
  }
`;
const GET_CONTACT_NOTE = gql`
  query GetContactNote($id: Int!) {
    contactNote(id: $id) {
      id
      text
      contact {
        id
      }
      user {
        id
      }
    }
  }
`;

const upsertContactNoteSchema = zod.object({
  note: zod.string(),
  contactId: zod.number().nullable(),
});

type UpsertContactNoteFormValues = zod.infer<typeof upsertContactNoteSchema>;

const TitleInputContainer = styled(FlexRow)`
  padding: 8px 10px;
  min-height: 100px;
  width: 100%;

  ${up('md')} {
    padding: 10px 8px;
    min-height: 62px;
  }
`;

const Spacer = styled(FlexColumn)`
  ${up('md')} {
    height: 158px;
  }
`;

// Free-text inputs are saved when the user leaves them rather than while typing.
const AUTOSAVE_ON_BLUR_FIELDS: Path<UpsertContactNoteFormValues>[] = ['note'];

export const ContactNoteInlineFormModal = memo(() => {
  const currentInstitution = useCurrentInstitution();
  const modalVisibilityHandler = useVisible(true);
  const { goBack } = useModalUrl({
    backgroundLocation: `/i/company/${currentInstitution.id}/contacts`,
  });
  const { t } = useTranslation(['common', 'contacts']);
  const handlecloseModal = () => {
    createContactNoteForm.reset();
    goBack();
    modalVisibilityHandler.close();
  };
  const { contactNoteId } = useParams<{
    contactNoteId?: string;
  }>();
  const contactId = new URLSearchParams(window.location.search).get('contactId');
  const isCreatingContactNote = !contactNoteId;
  const showNotification = useShowNotification();
  const showConfirmationModal = useShowConfirmationModal();
  const { getEditContactNoteUrl } = useUrls();
  const { hasPermission } = useAuthorization();
  const { id: currentUserId } = useCurrentUser();
  const [isNoteOwner, setIsNoteOwner] = useState(false);

  const handleGetContactNoteError = useCallback(() => {
    modalVisibilityHandler.close();
    goBack();
    showNotification({
      title: t('contacts:contactNoteInlineForm.errors.notFound.title'),
      message: t('contacts:contactNoteInlineForm.errors.notFound.message'),
      type: 'error',
    });
  }, [modalVisibilityHandler, goBack, showNotification, t]);

  const handleGraphQLError = useHandleGraphQLError();
  const showCloseConfirmationModal = useShowCloseConfirmationModal();

  const [defaultValues, setDefaultValues] = useState<UpsertContactNoteFormValues>({
    note: '',
    contactId: contactId ? Number(contactId) : null,
  });

  const createContactNoteForm = useForm<UpsertContactNoteFormValues>({
    resolver: zodResolver(
      upsertContactNoteSchema.extend({
        note: zod.string().min(1, {
          message: t('contacts:contactNoteInlineForm.fields.note.errors.required'),
        }),
        contactId: zod.number(),
      }),
    ),
    defaultValues,
  });

  const { loading: getContactNoteLoading } = useQuery<
    Pick<Query, 'contactNote'>,
    QueryContactNoteArgs
  >(GET_CONTACT_NOTE, {
    skip: !contactNoteId,
    variables: {
      id: Number(contactNoteId),
    },
    onError: handleGetContactNoteError,
    onCompleted: (data) => {
      const { text, contact, user } = data.contactNote;
      const input = {
        note: text,
        contactId: contact?.id ?? null,
      };
      setDefaultValues({
        note: text,
        contactId: contact?.id ?? null,
      });

      if (user?.id && user.id === currentUserId) {
        setIsNoteOwner(true);
      }

      createContactNoteForm.reset(input);
    },
  });

  const [createContactNote, { loading: createContactNoteLoading }] = useMutation<
    Pick<Mutation, 'createContactNote'>,
    MutationCreateContactNoteArgs
  >(CREATE_CONTACT_NOTE_MUTATION);
  const [updateContactNote, { loading: updateContactNoteLoading }] = useMutation<
    Pick<Mutation, 'updateContactNote'>,
    MutationUpdateContactNoteArgs
  >(UPDATE_CONTACT_NOTE_MUTATION);
  const [deleteContactNote, { loading: deleteContactNoteLoading }] = useMutation<
    Pick<Mutation, 'deleteContactNote'>,
    MutationDeleteContactNoteArgs
  >(DELETE_CONTACT_NOTE_MUTATION);

  const {
    formState: { isDirty, dirtyFields },
  } = createContactNoteForm;

  const isFormDirty = isDirty && Object.keys(dirtyFields).length > 0;

  const hasNoteManagePermission = useMemo(
    () => hasPermission('crm:company.contact_note:update:all'),
    [hasPermission],
  );

  const canDeleteNote = useMemo(
    () => isNoteOwner || hasNoteManagePermission,
    [isNoteOwner, hasNoteManagePermission],
  );

  const handleCacheUpdate = useCallback(
    (cache: ApolloCache<any>) => {
      const normalizedId = cache.identify({
        id: Number(contactNoteId),
        __typename: 'ContactNote',
      });
      cache.evict({ id: normalizedId });
      cache.gc();
    },
    [contactNoteId],
  );

  const updateContactNoteRecord = useCallback(
    async (
      values: Partial<UpsertContactNoteFormValues>,
      { onCompleted }: { onCompleted: () => void },
    ) => {
      await updateContactNote({
        variables: {
          input: {
            note: values.note!,
            institutionId: currentInstitution.id,
            contactNoteId: Number(contactNoteId),
          },
        },
        onCompleted,
        onError: handleGraphQLError,
      });
    },
    [updateContactNote, currentInstitution.id, contactNoteId, handleGraphQLError],
  );

  const onAutosaveContactNote = useCallback(
    async (values: Partial<UpsertContactNoteFormValues>) => {
      await updateContactNoteRecord(values, {
        onCompleted: () => {
          (Object.keys(values) as (keyof UpsertContactNoteFormValues)[]).forEach(
            (name) => {
              if (isEqual(createContactNoteForm.getValues(name), values[name])) {
                createContactNoteForm.resetField(name, { defaultValue: values[name] });
              }
            },
          );
        },
      });
    },
    [updateContactNoteRecord, createContactNoteForm],
  );

  return (
    <InlineFormModal<UpsertContactNoteFormValues>
      title={
        isCreatingContactNote
          ? t('contacts:contactNoteInlineForm.title.create')
          : t('contacts:contactNoteInlineForm.title.update')
      }
      submitButtonText={t('contacts:contactNoteInlineForm.submitButtonText.create')}
      autoSaveEnabled={!isCreatingContactNote}
      onAutosave={onAutosaveContactNote}
      autosaveOnBlurFields={AUTOSAVE_ON_BLUR_FIELDS}
      testId="upsert-contact-note-modal"
      isLoading={getContactNoteLoading}
      disableSubmitButton={!isCreatingContactNote && !isNoteOwner}
      isSubmitting={createContactNoteLoading || updateContactNoteLoading}
      visibilityHandler={modalVisibilityHandler}
      form={createContactNoteForm}
      onCancel={() => {
        if (!isFormDirty) {
          handlecloseModal();
          return;
        }

        showCloseConfirmationModal({
          onCancel: handlecloseModal,
        });
      }}
      onSubmit={async (values) => {
        await createContactNote({
          variables: {
            institutionId: currentInstitution.id,
            input: {
              note: values.note!,
              contactId: values.contactId!,
            },
          },
          onCompleted: () => {
            showNotification({
              title: t('contacts:contactNoteInlineForm.notifications.create'),
              type: 'success',
            });
            handlecloseModal();
          },
          onError: handleGraphQLError,
          update: handleCacheUpdate,
        });
      }}
      topContent={
        <Controller
          control={createContactNoteForm.control}
          name="note"
          render={({ field }) => (
            <TitleInputContainer>
              <BigTextArea
                {...field}
                data-lgg-id="upsert-contact-note-field-note"
                showErrorMessage
                error={createContactNoteForm.formState.errors.note?.message}
                placeholder={t('contacts:contactNoteInlineForm.fields.note.placeholder')}
                label={t('contacts:contactNoteInlineForm.fields.note.label')}
              />
            </TitleInputContainer>
          )}
        />
      }
      bottomContent={<Spacer />}
      moreOptions={
        isCreatingContactNote
          ? []
          : [
              {
                icon: 'link',
                label: t('contacts:contactNoteInlineForm.moreOptions.copyNoteLink.label'),
                onClick: () => {
                  copyUrlToClipboard(
                    getEditContactNoteUrl({
                      contactNoteId: Number(contactNoteId),
                      contactId: Number(contactId),
                      options: { includeHost: true },
                    }),
                  );
                  showNotification({
                    title: t(
                      'contacts:contactNoteInlineForm.moreOptions.copyNoteLink.successMessage',
                    ),
                    type: 'success',
                  });
                },
              },
              ...(canDeleteNote
                ? [
                    {
                      label: t(
                        'contacts:contactNoteInlineForm.moreOptions.deleteNote.label',
                      ),
                      render: () => {
                        return (
                          <InlineFormDeleteOption
                            label={t(
                              'contacts:contactNoteInlineForm.moreOptions.deleteNote.label',
                            )}
                            icon="deleteBin"
                            disabled={deleteContactNoteLoading}
                            onClick={async () => {
                              showConfirmationModal({
                                title: t(
                                  'contacts:contactNoteInlineForm.moreOptions.deleteNote.label',
                                ),
                                message: t(
                                  'contacts:contactNoteInlineForm.moreOptions.deleteNote.message',
                                ),
                                confirmButtonText: t(
                                  'contacts:contactNoteInlineForm.moreOptions.deleteNote.label',
                                ),
                                confirmButtonType: 'delete',
                                testId: 'delete-contact-note-confirmation-modal',
                                onConfirm: async () => {
                                  await deleteContactNote({
                                    variables: {
                                      input: {
                                        contactId: Number(contactId),
                                        contactNoteId: Number(contactNoteId),
                                        institutionId: currentInstitution.id,
                                      },
                                    },
                                    onCompleted: () => {
                                      showNotification({
                                        title: t(
                                          'contacts:contactNoteInlineForm.moreOptions.deleteNote.successMessage',
                                        ),
                                        type: 'success',
                                      });
                                      handlecloseModal();
                                    },
                                    onError: (error) => {
                                      handleGraphQLError(error);
                                    },
                                    update: handleCacheUpdate,
                                  });
                                },
                              });
                            }}
                          />
                        );
                      },
                    },
                  ]
                : []),
            ]
      }
    >
      <FlexColumn>
        <Controller
          control={createContactNoteForm.control}
          name="contactId"
          render={({ field }) => {
            const error = createContactNoteForm.formState.errors.contactId?.message;

            return (
              <InlineInputWrapper
                label={t('contacts:contactNoteInlineForm.fields.relatedContact.label')}
                error={error}
                icon="contact"
                showErrorMessage={false}
              >
                <InlineFormContactSelect
                  selectedContactIds={field.value ? [field.value] : []}
                  isMulti={false}
                  onChange={(value) => {
                    if (value) {
                      field.onChange(value.value === field.value ? null : value.value);
                    }
                  }}
                  isDisabled={!isCreatingContactNote}
                  hasError={Boolean(error)}
                  isClearable={false}
                  hideIndicators
                  name="upsert-contact-note-field-related-contact"
                  mobileLabel={t(
                    'contacts:contactNoteInlineForm.fields.relatedContact.label',
                  )}
                />
              </InlineInputWrapper>
            );
          }}
        />
      </FlexColumn>
    </InlineFormModal>
  );
});
